<script lang="ts">
  import AuctionCard from '$lib/components/AuctionCard.svelte';
  import H1 from '$lib/components/H1.svelte';
  import LotCard from '$lib/components/LotCard.svelte';
  import { writable } from 'svelte/store';
  import type { PageData } from './$types';
  import Hero from '$lib/components/heroes/Hero.svelte';
  import Head from '$lib/components/Head.svelte';
  import { session } from '$lib/stores';
  import { flip } from 'svelte/animate';
  import { Icon } from '@steeze-ui/svelte-icon';
  import { Bars3 } from '@steeze-ui/heroicons';
  import { onMount } from 'svelte';
  import { websocketClient } from '$lib/WebSocket';
  import { invalidateAll } from '$app/navigation';

  export let data: PageData;

  // Draggable
  let draggable = false;
  let hovering: number | null = null;

  $: draggable = $session?.role == 'admin';

  const drop = (
    event: DragEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    },
    target: number
  ) => {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
      const start = parseInt(event.dataTransfer.getData('text/plain'));
      const newTracklist = data.auctions;

      if (start < target) {
        newTracklist.splice(target + 1, 0, newTracklist[start]);
        newTracklist.splice(start, 1);
      } else {
        newTracklist.splice(target, 0, newTracklist[start]);
        newTracklist.splice(start + 1, 1);
      }
      data.auctions = newTracklist;

      fetch('/api/dashboard/auctions/sort', {
        method: 'PUT',
        body: JSON.stringify(data.auctions.map((a) => a.id))
      });
    }
    hovering = null;
  };

  const dragstart = (
    event: DragEvent & {
      currentTarget: EventTarget & HTMLDivElement;
    },
    start: number
  ) => {
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
      event.dataTransfer.setData('text/plain', `${start}`);
    }
  };

  onMount(() => {
    const ws = websocketClient();
    const cleanup = ws.onLotUpdated(() => {
      invalidateAll();
    });
    return cleanup;
  });
</script>

<Head />

<Hero heros={data.heros} />

{#if data.auctions?.length > 0}
  <div
    class="max-w-7xl mx-auto mt-8 px-6 py-8 sm:px-2 lg:px-4 w-full bg-white rounded-none md:rounded-md"
  >
    <H1 class="text-center mb-6">
      {#if data.auctions.length == 1}
        Subasta destacada
      {:else}
        Subastas destacadas
      {/if}
    </H1>

    <div class="space-y-16 pt-6 mt-6 border-t border-gray-200">
      {#each data.auctions as auction, i (auction.id)}
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="flex"
          animate:flip
          {draggable}
          on:dragstart={(event) => dragstart(event, i)}
          on:drop|preventDefault={(event) => drop(event, i)}
          on:dragover|preventDefault
          on:dragenter={() => (hovering = i)}
          class:opacity-50={hovering === i}
        >
          {#if draggable}
            <div class="flex justify-center items-center">
              <Icon src={Bars3} size="24" />
            </div>
          {/if}
          <AuctionCard {auction} />
        </div>
      {/each}
    </div>
  </div>
{/if}

{#await data.streamed.lots}
  <span></span>
{:then lots}
  {#if lots.length > 0}
    <div class="flex flex-col grow mt-8 px-6 lg:px- mx-auto max-w-7xl md:space-x-10 min-h-min">
      <div class="max-w-7xl mx-auto py-8 sm:px-2 lg:px-4 w-full">
        <H1 class="text-center mb-6">Lotes destacados</H1>

        <div class="space-y-16 pt-6 mt-6">
          <div
            class="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8"
          >
            {#each lots as lot}
              <LotCard {lot} />
            {/each}
          </div>
        </div>
      </div>
    </div>
  {/if}
{/await}
